import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { UniversalConfig } from '@application/universal_config.service';
import { NavigationGuardProvider } from '@cortex-shim/navigation_guard_context';
import { ProgrammaticBrowserRouter } from '@cortex-shim/programmatic_browser_router.component';
import { OutletPage } from '@old_patient_dependencies/outlet_page.component';
import { LocationSelectorService } from '@public/src/application/services/location_selector.service';
import { PatientFlowContainer } from '@public/src/components/patient_flow_container.component';
import { sharedResolution } from '@public/src/shared_resolution';
import { createRoot, Root } from 'react-dom/client';
import { Route, Routes } from 'react-router';
import { from, Observable } from 'rxjs';
import { ViewAnonymousChat } from '../../../patient/src/views/view_anonymous_chat/view_anonymous_chat.component';
import { ComponentCanDeactivate } from '../app/Guards/confirm-leave.guard';
import { NavigationGuardService } from '../app/Services/navigation-guard.service';
import { CellulaProviderWrapper } from './cellula_provider_wrapper.component';

const containerElementName = 'AnonymousChatComponent';
const FULL_HEIGHT_CLASS_NAME = 'js-fullHeight';

@Component({
	standalone: false,
	styleUrls: ['./css/anonymous-chat.wrapper.scss'],
	selector: 'anonymous-chat-component',
	template: ` <semantic-view hideOnMobile="true">
		<header-component header></header-component>

		<div class="wrapper-container" #${containerElementName}></div>

		<footer-component footer></footer-component>
	</semantic-view>`,
	encapsulation: ViewEncapsulation.None,
})
export class AnonymousChatWrapper implements OnDestroy, AfterViewInit, ComponentCanDeactivate {
	@ViewChild(containerElementName, { static: false }) containerRef: ElementRef;

	private root: Root;
	private beforeUnloadHandler: (e: BeforeUnloadEvent) => string | void;

	constructor(
		private readonly ngZone: NgZone,
		private readonly navigationGuardService: NavigationGuardService,
	) {
		this.beforeUnloadHandler = (e: BeforeUnloadEvent) => {
			if (this.navigationGuardService.shouldBlock()) {
				e.preventDefault();
				e.returnValue = '';
				return '';
			}
		};
	}

	ngAfterViewInit() {
		document.body.classList.add(FULL_HEIGHT_CLASS_NAME);
		window.addEventListener('beforeunload', this.beforeUnloadHandler);
		this.render();
	}

	ngOnDestroy() {
		document.body.classList.remove(FULL_HEIGHT_CLASS_NAME);
		window.removeEventListener('beforeunload', this.beforeUnloadHandler);
		this.root?.unmount();
		this.navigationGuardService.clearGuard();
	}

	canDeactivate(): Observable<boolean> | boolean {
		const result = this.navigationGuardService.canNavigate();

		if (result instanceof Promise) {
			return from(result);
		}

		return result;
	}

	private async render() {
		const locationSelectorService = sharedResolution.resolve(LocationSelectorService);
		const universalConfigService = sharedResolution.resolve(UniversalConfig);

		const root = createRoot(this.containerRef.nativeElement);
		this.root = root;

		const split = locationSelectorService.location.route.split('/');
		split.splice(0, 1);
		const routeBase = split.join('/');

		// Create the navigation guard context value
		const navigationGuardContextValue = {
			setGuard: (handler: () => boolean) => {
				this.navigationGuardService.setGuard(handler);
			},
			setShowDialogHandler: (handler: () => Promise<boolean>) => {
				this.navigationGuardService.setShowDialogHandler(handler);
			},
			clearGuard: () => {
				this.navigationGuardService.clearGuard();
			},
		};

		this.ngZone.runOutsideAngular(() => {
			root.render(
				<NavigationGuardProvider value={navigationGuardContextValue}>
					<CellulaProviderWrapper useGlobals>
						<PatientFlowContainer>
							<ProgrammaticBrowserRouter basename={universalConfigService.alias} forceNewHistory>
								<Routes>
									<Route path={routeBase} element={<OutletPage />}>
										<Route path='message/anonymous-chat/*' element={<ViewAnonymousChat />} />
									</Route>
								</Routes>
							</ProgrammaticBrowserRouter>
						</PatientFlowContainer>
					</CellulaProviderWrapper>
				</NavigationGuardProvider>,
			);
		});
	}
}
